import React, { useRef, useState, useCallback } from 'react'
import { cn, ArrowDown } from 'design-system'

import useKeyHandlers from 'src/hooks/useKeyHandlers'

import { ExpandVertical } from 'src/containers/animations/ExpandVertical'

/** `aria-level` value matching outline position of `<h2>`. */
export const H2 = 2

/** `aria-level` value matching outline position of `<h3>`. */
const H3 = 3

/**
 *
 * @param items
 * An array of objects { key: string, term: string, detail: JSX }
 *
 * Full example:
 * [{ key: 'unique-string', term: 'Hello world!', detail: <p>A great phrase</p> }, ...]
 * @param props.sectionHeadingAriaLevel 1–6; overrides each section title’s `role="heading"` level (default `H3`).
 */

const Accordion = ({
  items,
  className,
  sectionHeadingAriaLevel = H3,
  ...props
}) => (
  <dl className={cn('overflow-hidden', className)} {...props}>
    {items.map((item, index) => (
      <AccordionSection
        key={item.key}
        item={item}
        index={index}
        sectionHeadingAriaLevel={sectionHeadingAriaLevel}
      />
    ))}
  </dl>
)

const AccordionSection = ({
  item: { key, term, desc, detail },
  index,
  sectionHeadingAriaLevel = H3
}) => {
  const [detailVisible, setDetailVisible] = useState(false)
  const [accordionNode, setAccordionNode] = useState(null)
  const getAccordionNode = useCallback(setAccordionNode, null)

  const buttonRef = useRef()
  const detailKey = `${key}-detail`

  const toggleDetail = () => setDetailVisible(!detailVisible)

  useKeyHandlers(
    accordionNode,
    {
      ESC: () => {
        setDetailVisible(false)
        buttonRef.current.focus()
      }
    },
    [accordionNode, setDetailVisible, buttonRef]
  )

  return (
    <div ref={getAccordionNode}>
      <dt>
        <button
          ref={buttonRef}
          className={cn('w-full bg-white py-4', {
            'border-0 border-t border-solid border-gray-400': index > 0
          })}
          aria-expanded={detailVisible}
          aria-controls={detailKey}
          onClick={toggleDetail}>
          <div className="flex items-start justify-between">
            <div className="text-left">
              <span
                role="heading"
                aria-level={sectionHeadingAriaLevel}
                className="block text-lg font-medium text-black">
                {term}
              </span>
              {desc && (
                <p className="text-sm font-light text-gray-700">{desc}</p>
              )}
            </div>
            <ArrowDown
              size={20}
              className={cn('shrink-0 text-gray-700 transition-transform', {
                '-rotate-180': detailVisible
              })}
              aria-hidden="true"
            />
          </div>
        </button>
      </dt>
      <dd id={detailKey}>
        <ExpandVertical expanded={detailVisible}>{detail}</ExpandVertical>
      </dd>
    </div>
  )
}

export default Accordion
